import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// Project data
const projectData = [
  {
    id: 1,
    title: "Durham Bus Transit Facility",
    category: "Transit / Public Sector",
    description: "MEP condition assessments and QA/QC commissioning support for a $85M FTA-funded transit program targeting LEED Gold — assessing existing buildings while new facilities are constructed.",
    image: "/images/fay-street-boiler-plant.jpeg",
    stats: {
      energySavings: "$85M",
      waterSavings: "4+",
      carbonReduction: "2 yrs"
    },
    statLabels: {
      energySavings: "Program Value",
      waterSavings: "Assessments Completed",
      carbonReduction: "Construction Timeline"
    },
    features: [
      "MEP field assessments across 5 buildings at 1903–1911 Fay Street, Durham, NC",
      "Commissioning QA/QC for new Paratransit and City Bus facilities — both targeting LEED Gold certification",
      "Weighted asset scoring and capital planning via MEP Tools Facility Assessment",
      "VAV system anomaly documentation and corrective action recommendations",
      "New facilities targeting LEED Gold — commissioning is required (EAp1) and enhanced commissioning earns additional points",
      "BIM/COBie handoff integration planned at construction closeout"
    ]
  },
  {
    id: 2,
    title: "Duct Leakage Testing & TAB Verification",
    category: "New Construction",
    description: "SMACNA-compliant duct air leakage testing and airflow verification for new construction and renovation projects — documented in the field with instant pass/fail results.",
    image: "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&w=800&q=80",
    stats: {
      energySavings: "SMACNA",
      waterSavings: "Real-time",
      carbonReduction: "Cloud"
    },
    statLabels: {
      energySavings: "Standard",
      waterSavings: "Field Results",
      carbonReduction: "Documentation"
    },
    features: [
      "SMACNA leakage class calculations in the field — no spreadsheets",
      "Ebtron airflow sensor calibration and verification",
      "Pass/fail results with exportable documentation per duct section",
      "Powered by MEP Tools DALT and Ebtron Calibration modules",
      "Suitable for new construction, renovations, and commissioning"
    ]
  },
  {
    id: 3,
    title: "Facility Condition Assessment",
    category: "Existing Facilities",
    description: "Structured MEP condition assessments for existing buildings — scoring assets across mechanical, electrical, plumbing, and controls systems to prioritize capital investment.",
    image: "/images/ahu-interior-inspection.jpeg",
    stats: {
      energySavings: "4 Systems",
      waterSavings: "Weighted",
      carbonReduction: "Audit-Ready"
    },
    statLabels: {
      energySavings: "MEP Systems Scored",
      waterSavings: "Scoring Model",
      carbonReduction: "Reports"
    },
    features: [
      "MEP asset inventory across controls, electrical, mechanical, and plumbing",
      "Weighted scoring: age, remaining service life, visual inspection, operational test",
      "Building-level condition rating from Failed to Like New",
      "Prioritized repair and replacement recommendations for capital planning",
      "Audit-ready documentation suitable for FTA, GSA, and local government reporting"
    ]
  },
  {
    id: 4,
    title: "Boiler Energy Conservation Analysis",
    category: "Existing Facilities",
    description: "Energy conservation measure analysis for aging boiler systems — modeling fuel savings, efficiency improvements, and ROI to support capital planning decisions.",
    image: "/images/boiler-exhaust-stack.jpeg",
    stats: {
      energySavings: "4 ECMs",
      waterSavings: "5 Fuels",
      carbonReduction: "Full ROI"
    },
    statLabels: {
      energySavings: "ECM Types Modeled",
      waterSavings: "Fuel Types Supported",
      carbonReduction: "Analysis"
    },
    features: [
      "Model operating costs at minimum, maximum, and selected load",
      "Apply four ECMs: reduced hours, improved efficiency, load reduction, reduced days",
      "Supports natural gas, fuel oil, coal, dual-fuel, and electric systems",
      "Annual savings and simple payback period calculation",
      "Exportable results for capital planning and owner reporting"
    ]
  },
  {
    id: 5,
    title: "Commissioning Support Services",
    category: "New Construction",
    description: "Owner's-side commissioning support for new construction and major renovations — often to meet LEED or code requirements — verifying that the systems owners pay for actually perform.",
    image: "/images/pump-motor-verification.jpeg",
    stats: {
      energySavings: "QA/QC",
      waterSavings: "Closeout",
      carbonReduction: "Owner"
    },
    statLabels: {
      energySavings: "Verification",
      waterSavings: "Documentation",
      carbonReduction: "Acceptance"
    },
    features: [
      "Owner's project requirements (OPR) development and design review",
      "Construction-phase observation and deficiency tracking",
      "Functional performance testing for HVAC, electrical, plumbing, and controls",
      "Systems manual and O&M training coordination",
      "COBie/BIM closeout integration for seamless transition to facility operations"
    ]
  },
  {
    id: 6,
    title: "Preventive Maintenance Program Development",
    category: "Existing Facilities",
    description: "Building a structured PM program from scratch — tied directly to facility assessment data and compliance requirements, delivered via the MEP Tools PM Scheduler.",
    image: "/images/fieldwork-pump-gauges.jpeg",
    stats: {
      energySavings: "27+",
      waterSavings: "Compliance",
      carbonReduction: "Work Orders"
    },
    statLabels: {
      energySavings: "MEP Task Library",
      waterSavings: "Calendar",
      carbonReduction: "Reactive & PM"
    },
    features: [
      "MEP task library covering HVAC, electrical, plumbing, and fire & life safety",
      "Code-required inspection tracking (NFPA 25, NFPA 72, NFPA 10, backflow)",
      "Overdue and due-soon alerts with calendar view",
      "Reactive work order management with P1–P4 priority",
      "Tied directly to facility assessment asset inventory — no double entry"
    ]
  }
];

const Projects: React.FC = () => {
  const [filter, setFilter] = useState<string>("All");
  const [activeProject, setActiveProject] = useState<number | null>(null);
  
  // Get unique categories for filter
  const uniqueCategories = Array.from(new Set(projectData.map(project => project.category)));
  const categories = ["All", ...uniqueCategories];
  
  // Filter projects based on selected category
  const filteredProjects = filter === "All" 
    ? projectData 
    : projectData.filter(project => project.category === filter);
    
  const handleProjectClick = (projectId: number) => {
    setActiveProject(activeProject === projectId ? null : projectId);
  };
  
  return (
    <>
      {/* Page Header */}
      <section className="bg-dark text-white py-5 mt-5">
        <div className="container py-4">
          <div className="row">
            <div className="col-lg-8">
              <h1 className="display-5 fw-bold mb-3">Our Projects</h1>
              <p className="lead mb-0">A look at our MEP assessment engagements, energy analyses, and the commissioning support that backs them up.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Gallery */}
      <section className="section section-light">
        <div className="container">
          <div className="row mb-5">
            <div className="col-12">
              <div className="d-flex flex-wrap justify-content-center gap-2">
                {categories.map((category, index) => (
                  <button 
                    key={index} 
                    className={`btn ${filter === category ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setFilter(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <div className="row g-4">
            {filteredProjects.map((project) => (
              <div key={project.id} className="col-md-6 col-lg-4" data-aos="fade-up">
                <div 
                  className={`card project-card-large h-100 overflow-hidden ${activeProject === project.id ? 'active' : ''}`}
                  onClick={() => handleProjectClick(project.id)}
                >
                  <div className="position-relative" style={{ height: "250px" }}>
                    <img 
                      src={project.image} 
                      alt={project.title} 
                      className="img-fluid w-100 h-100 object-fit-cover" 
                    />
                    <div className="position-absolute top-0 start-0 m-3">
                      <span className="badge bg-primary px-3 py-2 rounded-pill">
                        {project.category}
                      </span>
                    </div>
                  </div>
                  <div className="card-body">
                    <h4 className="card-title mb-3">{project.title}</h4>
                    <p className="card-text">{project.description}</p>
                    
                    {activeProject === project.id && (
                      <div className="project-details mt-4">
                        <div className="row g-3 mb-4">
                          <div className="col-4 text-center border-end">
                            <div className="fw-bold text-primary">{project.stats.energySavings}</div>
                            <small>{(project as any).statLabels?.energySavings ?? 'Stat 1'}</small>
                          </div>
                          <div className="col-4 text-center border-end">
                            <div className="fw-bold text-primary">{project.stats.waterSavings}</div>
                            <small>{(project as any).statLabels?.waterSavings ?? 'Stat 2'}</small>
                          </div>
                          <div className="col-4 text-center">
                            <div className="fw-bold text-primary">{project.stats.carbonReduction}</div>
                            <small>{(project as any).statLabels?.carbonReduction ?? 'Stat 3'}</small>
                          </div>
                        </div>
                        
                        <h5 className="mb-3">Key Details</h5>
                        <ul className="list-unstyled mb-0">
                          {project.features.map((feature, index) => (
                            <li key={index} className="mb-2 d-flex">
                              <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <button 
                      className={`btn btn-link text-primary p-0 mt-3 d-flex align-items-center ${activeProject === project.id ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProjectClick(project.id);
                      }}
                    >
                      {activeProject === project.id ? (
                        <>View Less <i className="bi bi-chevron-up ms-1"></i></>
                      ) : (
                        <>View Details <i className="bi bi-chevron-down ms-1"></i></>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Highlight */}
      <section className="section bg-eco-pattern">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">Featured <span className="text-primary">Case Study</span></h2>
              <p className="lead">Fay Street Administration Buildings — a completed mechanical assessment, photographed in the field.</p>
            </div>
          </div>

          <div className="row g-5 align-items-center">
            <div className="col-lg-6" data-aos="fade-right">
              <img
                src="/images/hero-boiler-plant.jpeg"
                alt="Exterior boiler plant at 1903 Fay Street, Durham, NC"
                className="img-fluid rounded-4 shadow-lg mb-3"
              />
              <div className="row g-3">
                <div className="col-4">
                  <img src="/images/fieldwork-pump-gauges.jpeg" alt="Heating water pump and pressure gauges inspection" className="img-fluid rounded-3 shadow-sm w-100 object-fit-cover" style={{ height: '110px' }} />
                </div>
                <div className="col-4">
                  <img src="/images/boiler-exhaust-stack.jpeg" alt="Boiler and exhaust stack close-up" className="img-fluid rounded-3 shadow-sm w-100 object-fit-cover" style={{ height: '110px' }} />
                </div>
                <div className="col-4">
                  <img src="/images/ahu-interior-inspection.jpeg" alt="Air handling unit interior insulation inspection" className="img-fluid rounded-3 shadow-sm w-100 object-fit-cover" style={{ height: '110px' }} />
                </div>
              </div>
            </div>
            <div className="col-lg-6" data-aos="fade-left">
              <div className="d-flex align-items-center gap-3 mb-3">
                <h3 className="text-primary mb-0">Fay Street Administration Buildings</h3>
                <span className="badge bg-success">Completed 2026</span>
              </div>
              <p className="text-muted mb-2">Durham, NC &middot; Part of the GO Durham transit facilities program with <strong>Teeple Architects</strong></p>
              <p className="lead mb-4">Three administration buildings needed to stay reliable for 2–5 more years while new transit facilities are designed and built. We assessed the mechanical systems in all three — boiler plants, air handling units, pumps, VAV terminals, and building automation — and delivered a prioritized roadmap the owner could act on immediately.</p>

              <div className="mb-4">
                <h5 className="mb-3">What We Did</h5>
                <ul className="list-unstyled">
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Visual inspection and photographic documentation of every major piece of mechanical equipment across three buildings</span>
                  </li>
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Coordinated the owner's HVAC, test-and-balance, and BAS contractors to evaluate control system operation and sensor calibration — surfacing VAV airflow issues invisible to visual inspection</span>
                  </li>
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Delivered 30+ prioritized repair and replacement recommendations, from immediate actions to capital planning items</span>
                  </li>
                  <li className="d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Confirmed the buildings can perform as intended through construction — with only one major equipment replacement required</span>
                  </li>
                </ul>
              </div>

              <div className="row g-3 mb-4">
                <div className="col-4 text-center">
                  <div className="fw-bold text-primary fs-4">3</div>
                  <small>Buildings Assessed</small>
                </div>
                <div className="col-4 text-center">
                  <div className="fw-bold text-primary fs-4">30+</div>
                  <small>Prioritized Recommendations</small>
                </div>
                <div className="col-4 text-center">
                  <div className="fw-bold text-primary fs-4">7 wks</div>
                  <small>Kickoff to Final Report</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Process */}
      <section className="section section-light">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">Our Project <span className="text-primary">Process</span></h2>
              <p className="lead">How we approach each MEP consulting engagement — from initial assessment through construction closeout.</p>
            </div>
          </div>
          
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="position-relative">
                <div className="position-absolute top-0 bottom-0 start-50 translate-middle-x" style={{ width: '2px', backgroundColor: 'var(--primary-light)' }}></div>
                
                <div className="row g-0">
                  <div className="col-md-6 position-relative mb-5" data-aos="fade-right">
                    <div className="pe-md-5 me-md-5">
                      <div className="position-relative bg-white p-4 rounded shadow-sm">
                        <div className="position-absolute top-50 end-0 translate-middle-y translate-middle-x">
                          <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.5rem', zIndex: 1 }}>
                            1
                          </div>
                        </div>
                        <h4>Kickoff & Scoping</h4>
                        <p className="mb-0">We meet with stakeholders and facility staff to understand the buildings, the concerns, and the timeline — and define exactly what the engagement will cover.</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6"></div>
                </div>
                
                <div className="row g-0">
                  <div className="col-md-6"></div>
                  <div className="col-md-6 position-relative mb-5" data-aos="fade-left">
                    <div className="ps-md-5 ms-md-5">
                      <div className="position-relative bg-white p-4 rounded shadow-sm">
                        <div className="position-absolute top-50 start-0 translate-middle-y translate-middle">
                          <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.5rem', zIndex: 1 }}>
                            2
                          </div>
                        </div>
                        <h4>Document Review</h4>
                        <p className="mb-0">We review whatever exists — drawings, specifications, sequences of operation, service records — to understand design intent before setting foot on site.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row g-0">
                  <div className="col-md-6 position-relative mb-5" data-aos="fade-right">
                    <div className="pe-md-5 me-md-5">
                      <div className="position-relative bg-white p-4 rounded shadow-sm">
                        <div className="position-absolute top-50 end-0 translate-middle-y translate-middle-x">
                          <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.5rem', zIndex: 1 }}>
                            3
                          </div>
                        </div>
                        <h4>Field Assessment & Testing</h4>
                        <p className="mb-0">On-site inspection and photographic documentation of equipment, coordinated with service contractors and BAS providers — including functional testing where warranted.</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6"></div>
                </div>
                
                <div className="row g-0">
                  <div className="col-md-6"></div>
                  <div className="col-md-6 position-relative mb-5" data-aos="fade-left">
                    <div className="ps-md-5 ms-md-5">
                      <div className="position-relative bg-white p-4 rounded shadow-sm">
                        <div className="position-absolute top-50 start-0 translate-middle-y translate-middle">
                          <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.5rem', zIndex: 1 }}>
                            4
                          </div>
                        </div>
                        <h4>Findings & Prioritized Report</h4>
                        <p className="mb-0">A written report with photographic evidence and prioritized recommendations — separating immediate actions from near-term repairs and capital planning items.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row g-0">
                  <div className="col-md-6 position-relative" data-aos="fade-right">
                    <div className="pe-md-5 me-md-5">
                      <div className="position-relative bg-white p-4 rounded shadow-sm">
                        <div className="position-absolute top-50 end-0 translate-middle-y translate-middle-x">
                          <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.5rem', zIndex: 1 }}>
                            5
                          </div>
                        </div>
                        <h4>Ongoing Partnership</h4>
                        <p className="mb-0">Findings become living data — asset inventories and PM schedules carried forward in MEP Tools, so the report keeps working after the engagement ends.</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-primary text-white text-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <h2 className="mb-4">Ready to discuss your building assessment?</h2>
              <p className="lead mb-4">Contact us today to discuss your facility's performance, savings, and sustainability goals.</p>
              <Link to="/contact" className="btn btn-light btn-lg">
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Projects;